<template>
  <div class="layer-widget-panel scrollbar">
    <div class="measurements-container">
      <div class="measurements">
        <div class="form-group hidden position">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">X</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-x"
                v-model="xPosition"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">Y</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="yPosition"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>

        <div class="form-group hidden size">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">W</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-width"
                v-model="width"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">H</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="height"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="extra-icons">
        <LockIcon :locked="isWidgetLocked" />
        <VisibilityIcon :isVisible="isWidgetVisible" @clicked="updateVisibility" />
        <PixelIcon :usePixels="usePixels" @clicked="changeValuesMode" />
      </div>
    </div>

    <div class="form-group template-input name">
      <label for="template-name">Widget Name</label>
      <input
        class="form-control"
        type="text"
        id="template-name"
        maxlength="40"
        placeholder="Name"
        :disabled="isWidgetLockedByOverride"
        v-model="value.object.name"
      />
    </div>

    <div class="form-group">
      <BaseInput label="Address">
        <Multiselect
          :options="organisationAddresses"
          track-by="name"
          label="name"
          v-model="value.object.tenants.address"
          placeholder="Select an Address"
          :searchable="true"
          :allow-empty="false"
          open-direction="bottom"
          :loading="loadingAddresses"
          :showLabels="false"
          @select="handleAddressChanged"
        >
          <template slot="option" slot-scope="{ option }">
            <div>{{ option.name }}</div>
          </template>
          <template slot="noResult">
            <span>No addresses found</span>
          </template>
          <template slot="noOptions">
            <span>No addresses found</span>
          </template>
        </Multiselect>
      </BaseInput>
    </div>

    <div v-show="value.object.tenants.floors" class="form-group">
      <div class="form-label">Select floors</div>
      <div class="floors-container scrollbar">
        <div class="all-floors">
          <div class="floor-title">All floors</div>
          <div class="floor-switch">
            <div class="custom-control custom-switch check">
              <input
                type="checkbox"
                class="custom-control-input"
                id="all-floors"
                :disabled="isWidgetLockedByOverride"
                v-model="allFloorsVisible"
              />
              <label class="custom-control-label" for="all-floors"></label>
            </div>
          </div>
        </div>

        <draggable v-model="value.object.tenants.floors">
          <div v-for="(floor, index) in value.object.tenants.floors" :key="index" class="floor">
            <div class="left-section">
              <div class="drag-icon">
                <i class="material-icons-outlined align-middle">dehaze</i>
              </div>
              <input
                class="floor-title"
                type="text"
                :value="floor.displayFloorName || `VÅNING ${floor.floor}`"
                minlength="3"
                maxlength="40"
                @input="(event) => updateFloorDisplayName(floor, event)"
              />
              <div class="edit-icon" title="Click the title to edit">
                <i class="material-icons-outlined align-middle">edit</i>
              </div>
            </div>
            <div class="floor-switch">
              <div class="custom-control custom-switch check">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="`${floor}-${index}`"
                  :disabled="isWidgetLockedByOverride"
                  v-model="floor.visible"
                  @change="updateAllFloors"
                />
                <label class="custom-control-label" :for="`${floor}-${index}`"></label>
              </div>
            </div>
          </div>
        </draggable>
      </div>
    </div>

    <div class="form-group">
      <div class="form-label">Widget Type</div>
      <div class="buttons-container">
        <button
          type="button"
          :class="{
            'button-2-primary': !value.object.tenants.verticalMode,
            'button-2-secondary': value.object.tenants.verticalMode,
          }"
          @click="setVerticalMode(false)"
        >
          Horizontal
        </button>

        <button
          type="button"
          :class="{
            'button-2-primary': value.object.tenants.verticalMode,
            'button-2-secondary': !value.object.tenants.verticalMode,
          }"
          @click="setVerticalMode(true)"
        >
          Vertical
        </button>
      </div>
    </div>

    <div class="form-group form-range layout-input">
      <div class="slider-top">
        <div class="form-label">Number of floors on 1 slide</div>
        <div class="value">{{ value.object.tenants.floorsOnSlide }}</div>
      </div>
      <div class="slider-container">
        <BaseSlider
          :value="value.object.tenants.floorsOnSlide"
          :min="1"
          :max="12"
          @update:value="updateTenantsFloors"
        />
      </div>
    </div>

    <div class="form-group form-range layout-input">
      <div class="slider-top">
        <div class="form-label">Interval (seconds)</div>
        <div class="value">{{ value.object.tenants.interval }}</div>
      </div>
      <div class="slider-container">
        <BaseSlider
          :value="value.object.tenants.interval"
          :min="1"
          :max="120"
          @update:value="updateTenantsInterval"
        />
      </div>
    </div>

    <div class="form-group layout-input time">
      <div class="sub-section-title">Refresh every</div>
      <div class="time-container">
        <input class="form-control" type="number" id="layer-time" min="0" v-model="refreshTime" />

        <select
          class="custom-select"
          id="layer-time-unit"
          :disabled="isWidgetLockedByOverride"
          v-model="value.object.tenants.timeUnit"
          style="zoom: 1.1; height: 38px"
        >
          <option :value="TIME_UNITS.SECONDS.value" style="zoom: 1.1">
            {{ TIME_UNITS.SECONDS.title }}
          </option>
          <option :value="TIME_UNITS.MINUTES.value" style="zoom: 1.1">
            {{ TIME_UNITS.MINUTES.title }}
          </option>
          <option default :value="TIME_UNITS.HOURS.value" style="zoom: 1.1">
            {{ TIME_UNITS.HOURS.title }}
          </option>
          <option :value="TIME_UNITS.DAYS.value" style="zoom: 1.1">
            {{ TIME_UNITS.DAYS.title }}
          </option>
        </select>
      </div>
    </div>

    <div class="section-title">Visual Appearance</div>

    <div class="number-position-switch">
      <div class="floor-switch">
        <div class="custom-control custom-switch check">
          <input
            type="checkbox"
            class="custom-control-input"
            id="number-position-switch"
            v-model="value.object.tenants.numbersOnTheLeft"
          />
          <label class="custom-control-label" for="number-position-switch"></label>
        </div>
      </div>

      <BaseText variant="inputLabel">Numbers on the left</BaseText>
    </div>

    <div class="group-text-switch">
      <div class="floor-switch">
        <div class="custom-control custom-switch check">
          <input
            type="checkbox"
            class="custom-control-input"
            id="group-text-switch"
            v-model="value.object.tenants.groupNamesAndNumbers"
          />
          <label class="custom-control-label" for="group-text-switch"></label>
        </div>
      </div>

      <BaseText variant="inputLabel">Group names and numbers</BaseText>
    </div>

    <div class="hide-number-switch">
      <div class="floor-switch">
        <div class="custom-control custom-switch check">
          <input
            type="checkbox"
            class="custom-control-input"
            id="hide-number-switch"
            v-model="value.object.tenants.hideNumbers"
          />
          <label class="custom-control-label" for="hide-number-switch"></label>
        </div>
      </div>

      <BaseText variant="inputLabel">Hide numbers</BaseText>
    </div>

    <div class="pagination-switch">
      <div class="floor-switch">
        <div class="custom-control custom-switch check">
          <input
            type="checkbox"
            class="custom-control-input"
            id="pagination-switch"
            v-model="value.object.tenants.paginationInText"
          />
          <label class="custom-control-label" for="pagination-switch"></label>
        </div>
      </div>

      <BaseText variant="inputLabel">Pagination in text</BaseText>
    </div>

    <div class="short-naming-switch">
      <div class="floor-switch">
        <div class="custom-control custom-switch check">
          <input
            type="checkbox"
            class="custom-control-input"
            id="short-naming-switch"
            v-model="value.object.tenants.shortenNames"
          />
          <label class="custom-control-label" for="short-naming-switch"></label>
        </div>
      </div>

      <BaseText variant="inputLabel">Shorten Names</BaseText>
    </div>

    <div class="sub-section-title">Background</div>
    <div class="form-group hidden">
      <div class="form-row background-css">
        <color-picker
          class="col"
          input-el-id="layer-background-color"
          :disabled="isWidgetLockedByOverride"
          v-model="value.object.tenants.backgroundColor"
        />

        <div class="input-group col">
          <div class="input-label">
            <span class="input-group-text">
              <i class="material-icons-outlined align-middle">opacity</i>
            </span>
          </div>
          <input
            type="number"
            class="form-control"
            id="layer-opacity"
            min="0"
            max="100"
            :disabled="isWidgetLockedByOverride"
            v-model="backgroundOpacity"
          />
        </div>
      </div>
    </div>

    <div class="spacing">
      <div class="template-input form-group vertical-padding">
        <label for="text-size">Vertical Spacing</label>

        <input
          type="number"
          class="form-control"
          id="text-size"
          :disabled="isWidgetLockedByOverride"
          min="0"
          max="120"
          v-model="verticalPadding"
        />
      </div>

      <div class="template-input form-group vertical-padding">
        <label for="text-size">Top Padding</label>

        <input
          type="number"
          class="form-control"
          id="text-size"
          :disabled="isWidgetLockedByOverride"
          min="0"
          max="120"
          v-model="topPadding"
        />
      </div>
    </div>

    <div class="title-row">
      <div class="font-container form-group layout-input">
        <label for="template-name">Title Font</label>
        <FontSelector
          :value="value.object.tenants.titleFont"
          @update:value="(font) => updateTenantsFont(font, 'titleFont')"
        ></FontSelector>
      </div>

      <div class="template-input form-group">
        <label for="text-size">Text Size</label>

        <input
          type="number"
          class="form-control"
          id="text-size"
          :disabled="isWidgetLockedByOverride"
          min="4"
          max="120"
          v-model="titleTextSize"
        />
      </div>

      <div class="color-section">
        <ColorPicker v-model="value.object.tenants.titleBackgroundColor" :leftPosition="-210">
          <div class="color-button" title="Background Color">
            <img
              class="logo w-165"
              src="@/assets/icon/Colors.svg"
              alt="Room's Number Background Color"
            />
            <div
              class="color-bar"
              :style="{ backgroundColor: value.object.tenants.titleBackgroundColor }"
            ></div>
          </div>
        </ColorPicker>

        <ColorPicker v-model="value.object.tenants.titleFontColor" :leftPosition="-260">
          <div class="color-button" title="Title Font Color">
            <i class="material-icons-outlined align-middle">text_fields</i>
            <div
              class="color-bar"
              :style="{ backgroundColor: value.object.tenants.titleFontColor }"
            ></div>
          </div>
        </ColorPicker>
      </div>

      <div class="form-group alignment d-flex">
        <div class="alignments form-inline">
          <div class="alignment-mode align-content-center align-middle">
            <input
              type="radio"
              id="title-left-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.LEFT_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.titleAlignment"
            />
            <label for="title-left-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_left</i>
            </label>
          </div>

          <div class="alignment-mode">
            <input
              type="radio"
              id="title-center-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.CENTER_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.titleAlignment"
            />
            <label for="title-center-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_center</i>
            </label>
          </div>

          <div class="alignment-mode">
            <input
              type="radio"
              id="title-right-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.RIGHT_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.titleAlignment"
            />
            <label for="title-right-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_right</i>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="floor-row">
      <div class="font-container form-group layout-input">
        <label for="template-name">Name Font</label>
        <FontSelector
          :value="value.object.tenants.floorFont"
          @update:value="(font) => updateTenantsFont(font, 'floorFont')"
        ></FontSelector>
      </div>

      <div class="template-input form-group">
        <label for="text-size">Text Size</label>

        <input
          type="number"
          class="form-control"
          id="text-size"
          :disabled="isWidgetLockedByOverride"
          min="4"
          max="120"
          v-model="floorTextSize"
        />
      </div>

      <div class="color-section">
        <ColorPicker v-model="value.object.tenants.floorFontColor" :leftPosition="-260">
          <div class="color-button" title="Floors Font Color">
            <i class="material-icons-outlined align-middle">text_fields</i>
            <div
              class="color-bar"
              :style="{ backgroundColor: value.object.tenants.floorFontColor }"
            ></div>
          </div>
        </ColorPicker>
      </div>

      <div class="form-group alignment d-flex">
        <div class="alignments form-inline">
          <div class="alignment-mode align-content-center align-middle">
            <input
              type="radio"
              id="text-left-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.LEFT_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.textAlignment"
            />
            <label for="text-left-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_left</i>
            </label>
          </div>

          <div class="alignment-mode">
            <input
              type="radio"
              id="text-center-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.CENTER_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.textAlignment"
            />
            <label for="text-center-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_center</i>
            </label>
          </div>

          <div class="alignment-mode">
            <input
              type="radio"
              id="text-right-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.RIGHT_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.textAlignment"
            />
            <label for="text-right-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_right</i>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="room-row">
      <div class="font-container form-group layout-input">
        <label for="template-name">Rooms Font</label>
        <FontSelector
          :value="value.object.tenants.roomsFont"
          @update:value="(font) => updateTenantsFont(font, 'roomsFont')"
        ></FontSelector>
      </div>

      <div class="template-input form-group">
        <label for="text-size">Text Size</label>

        <input
          type="number"
          class="form-control"
          id="text-size"
          :disabled="isWidgetLockedByOverride"
          min="4"
          max="120"
          v-model="numberTextSize"
        />
      </div>

      <div class="color-section">
        <ColorPicker v-model="value.object.tenants.numberFontColor" :leftPosition="-260">
          <div class="color-button" title="Room's Number Font Color">
            <i class="material-icons-outlined align-middle">text_fields</i>
            <div
              class="color-bar"
              :style="{ backgroundColor: value.object.tenants.numberFontColor }"
            ></div>
          </div>
        </ColorPicker>
      </div>

      <div class="form-group alignment d-flex">
        <div class="alignments form-inline">
          <div class="alignment-mode align-content-center align-middle">
            <input
              type="radio"
              id="number-left-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.LEFT_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.numberAlignment"
            />
            <label for="number-left-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_left</i>
            </label>
          </div>

          <div class="alignment-mode">
            <input
              type="radio"
              id="number-center-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.CENTER_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.numberAlignment"
            />
            <label for="number-center-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_center</i>
            </label>
          </div>

          <div class="alignment-mode">
            <input
              type="radio"
              id="number-right-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.RIGHT_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.numberAlignment"
            />
            <label for="number-right-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_right</i>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="pagination-row">
      <div class="font-container form-group layout-input">
        <label for="template-name">Pagination Font</label>
        <FontSelector
          :value="value.object.tenants.paginationFont"
          @update:value="(font) => updateTenantsFont(font, 'paginationFont')"
        ></FontSelector>
      </div>

      <div class="template-input form-group">
        <label for="text-size">Text Size</label>

        <input
          type="number"
          class="form-control"
          id="text-size"
          :disabled="isWidgetLockedByOverride"
          min="4"
          max="120"
          v-model="paginationTextSize"
        />
      </div>

      <div class="color-section">
        <ColorPicker v-model="value.object.tenants.paginationFontColor" :leftPosition="-260">
          <div class="color-button" title="Pagination's Font Color">
            <i class="material-icons-outlined align-middle">text_fields</i>
            <div
              class="color-bar"
              :style="{ backgroundColor: value.object.tenants.paginationFontColor }"
            ></div>
          </div>
        </ColorPicker>
      </div>

      <div class="form-group alignment d-flex">
        <div class="alignments form-inline">
          <div class="alignment-mode align-content-center align-middle">
            <input
              type="radio"
              id="pagination-left-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.LEFT_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.paginationAlignment"
            />
            <label for="pagination-left-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_left</i>
            </label>
          </div>

          <div class="alignment-mode">
            <input
              type="radio"
              id="pagination-center-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.CENTER_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.paginationAlignment"
            />
            <label for="pagination-center-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_center</i>
            </label>
          </div>

          <div class="alignment-mode">
            <input
              type="radio"
              id="pagination-right-align"
              class="custom-control-input"
              hidden
              :value="ALIGN_MODES.RIGHT_ALIGN"
              :disabled="isWidgetLockedByOverride"
              v-model="value.object.tenants.paginationAlignment"
            />
            <label for="pagination-right-align" class="alignment-label">
              <i class="material-icons align-middle">format_align_right</i>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="group-text-switch">
      <div class="floor-switch">
        <div class="custom-control custom-switch check">
          <input
            type="checkbox"
            class="custom-control-input"
            id="addressswitch"
            v-model="value.object.tenants.showAddress"
          />
          <label class="custom-control-label" for="addressswitch"></label>
        </div>
      </div>

      <BaseText variant="inputLabel">Show Address</BaseText>
    </div>

    <template v-if="value.object.tenants.showAddress">
      <div class="form-group template-input">
        <label for="widget-title-text">Address Text</label>
        <input
          class="form-control"
          type="text"
          id="widget-title-text"
          maxlength="100"
          placeholder="Address Text"
          :disabled="isWidgetLockedByOverride"
          v-model="addressText"
        />
      </div>

      <div class="address-row">
        <div class="font-container form-group layout-input">
          <label for="template-name">Address Font</label>
          <FontSelector
            :value="value.object.tenants.addressFont"
            @update:value="(font) => updateTenantsFont(font, 'addressFont')"
          ></FontSelector>
        </div>

        <div class="template-input form-group">
          <label for="text-size">Text Size</label>

          <input
            type="number"
            class="form-control"
            id="text-size"
            :disabled="isWidgetLockedByOverride"
            min="4"
            max="120"
            v-model="addressTextSize"
          />
        </div>

        <div class="color-section">
          <ColorPicker v-model="addressFontColor" :leftPosition="-210">
            <div class="color-button" title="Address Font Color">
              <i class="material-icons-outlined align-middle">text_fields</i>
              <div class="color-bar" :style="{ backgroundColor: addressFontColor }"></div>
            </div>
          </ColorPicker>

          <ColorPicker v-model="addressBackgroundColor" :leftPosition="-260">
            <div class="color-button" title="Address Background Color">
              <img
                class="logo w-165"
                src="@/assets/icon/Colors.svg"
                alt="Address Background Color"
              />
              <div
                class="color-bar"
                :style="{
                  backgroundColor: addressBackgroundColor,
                }"
              ></div>
            </div>
          </ColorPicker>
        </div>
      </div>

      <div class="address-options">
        <div class="alignment d-flex">
          <div class="alignments form-inline">
            <div class="alignment-mode align-content-center align-middle">
              <input
                type="radio"
                id="address-left-align"
                class="custom-control-input"
                hidden
                :value="ALIGN_MODES.LEFT_ALIGN"
                :disabled="isWidgetLockedByOverride"
                v-model="value.object.tenants.addressAlignment"
              />
              <label for="address-left-align" class="alignment-label">
                <i class="material-icons align-middle">format_align_left</i>
              </label>
            </div>

            <div class="alignment-mode">
              <input
                type="radio"
                id="address-center-align"
                class="custom-control-input"
                hidden
                :value="ALIGN_MODES.CENTER_ALIGN"
                :disabled="isWidgetLockedByOverride"
                v-model="value.object.tenants.addressAlignment"
              />
              <label for="address-center-align" class="alignment-label">
                <i class="material-icons align-middle">format_align_center</i>
              </label>
            </div>

            <div class="alignment-mode">
              <input
                type="radio"
                id="address-right-align"
                class="custom-control-input"
                hidden
                :value="ALIGN_MODES.RIGHT_ALIGN"
                :disabled="isWidgetLockedByOverride"
                v-model="value.object.tenants.addressAlignment"
              />
              <label for="address-right-align" class="alignment-label">
                <i class="material-icons align-middle">format_align_right</i>
              </label>
            </div>
          </div>
        </div>

        <div class="form-group hidden size padding">
          <div class="form-row">
            <div class="input-group col" title="Vertical padding">
              <div class="input-label">
                <i class="material-icons-outlined align-middle">format_line_spacing</i>
              </div>
              <input
                type="number"
                class="form-control"
                id="address-vertical-padding"
                v-model="addressVerticalPadding"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col" title="Horizontal padding">
              <div class="input-label">
                <i class="material-icons-outlined align-middle rotate-icon">format_line_spacing</i>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="addressHorizontalPadding"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import { Sketch } from 'vue-color';
  import ClickOutside from 'vue-click-outside';
  import { Multiselect } from 'vue-multiselect';
  import draggable from 'vuedraggable';

  import ColorPicker from '@/components/ColorPicker';
  import PixelIcon from '@/components/layoutDesigner/editor/settingPanel/PixelIcon.vue';
  import LockIcon from '@/components/layoutDesigner/editor/settingPanel/LockIcon.vue';
  import FontSelector from '@/components/common/fontSelector/FontSelector.vue';
  import { BaseSlider } from '@ui/atoms/baseSlider';
  import BaseText from '@ui/atoms/baseText/BaseText.vue';
  import VisibilityIcon from '@/components/layoutDesigner/editor/settingPanel/VisibilityIcon.vue';

  import widgetMixin from '@/components/templates/editor/settingPanel/widgetMixin';
  import { restrictToBounds } from '@/helpers/draggable';

  import { TIME_UNITS } from '@/constant/const';
  import { apiGetAddresses, apiGetDetailedAddress } from '@/api/tenants';
  import { demoTenantAddress, tenantsDemoData } from '@/constant/tenants';
  import { ALIGN_MODES } from '@/models/layoutDesigner';
  import { Colors } from '@/constant/theme';

  export default {
    name: 'TenantsWidgetPanel',

    mixins: [widgetMixin],

    directives: {
      ClickOutside,
    },

    components: {
      ColorPicker,
      PixelIcon,
      LockIcon,
      BaseSlider,
      BaseText,
      FontSelector,
      Sketch,
      Multiselect,
      draggable,
      VisibilityIcon,
    },

    data() {
      return {
        TIME_UNITS,
        ALIGN_MODES,
        COLORS: Colors,
        showTitleColorPicker: false,
        showFloorsColorPicker: false,
        showRoomsColorPicker: false,
        showPaginationColorPicker: false,
        pickerColorKey: null,
        organisationAddresses: [],
        loadingAddresses: false,
      };
    },

    created() {
      this.getAddresses();
    },

    computed: {
      allFloorsVisible: {
        get() {
          return this.value.object.tenants.allFloorsVisible;
        },
        set(value) {
          this.value.object.tenants.floors.forEach((_, index) => {
            this.$set(this.value.object.tenants.floors[index], 'visible', value);
          });

          this.$set(this.value.object.tenants, 'allFloorsVisible', value);
        },
      },

      refreshTime: {
        get() {
          return this.value.object.tenants.refreshTime && this.value.object.tenants.refreshTime <= 0
            ? 1
            : this.value.object.tenants.refreshTime;
        },
        set(value = 0) {
          const refreshTime = restrictToBounds(!!value ? value : 0, 0, Infinity);
          this.$set(this.value.object.tenants, 'refreshTime', Number(refreshTime));
        },
      },

      backgroundOpacity: {
        get() {
          return this.value.object.tenants.opacity;
        },
        set(value = 100) {
          const refreshTime = restrictToBounds(value, 0, 100);
          this.$set(this.value.object.tenants, 'opacity', Number(refreshTime));
        },
      },

      addressFontColor: {
        get() {
          return this.value.object.tenants.addressFontColor || '#000000FF';
        },
        set(value) {
          this.$set(this.value.object.tenants, 'addressFontColor', value);
        },
      },

      addressBackgroundColor: {
        get() {
          return this.value.object.tenants.addressBackgroundColor || '#FFFFFFFF';
        },
        set(value) {
          this.$set(this.value.object.tenants, 'addressBackgroundColor', value);
        },
      },

      titleTextSize: {
        get() {
          return this.value.object.tenants.titleFontSize;
        },
        set(value) {
          this.$set(
            this.value.object.tenants,
            'titleFontSize',
            restrictToBounds(Number(value), 4, 120),
          );
        },
      },

      floorTextSize: {
        get() {
          return this.value.object.tenants.floorFontSize;
        },
        set(value) {
          this.$set(
            this.value.object.tenants,
            'floorFontSize',
            restrictToBounds(Number(value), 4, 120),
          );
        },
      },

      numberTextSize: {
        get() {
          return this.value.object.tenants.numberFontSize;
        },
        set(value) {
          this.$set(
            this.value.object.tenants,
            'numberFontSize',
            restrictToBounds(Number(value), 4, 120),
          );
        },
      },

      paginationTextSize: {
        get() {
          return this.value.object.tenants.paginationFontSize || 13;
        },
        set(value) {
          this.$set(
            this.value.object.tenants,
            'paginationFontSize',
            restrictToBounds(Number(value), 4, 120),
          );
        },
      },

      verticalPadding: {
        get() {
          return this.value.object.tenants.verticalPadding === undefined
            ? 8
            : this.value.object.tenants.verticalPadding;
        },
        set(value) {
          this.$set(
            this.value.object.tenants,
            'verticalPadding',
            restrictToBounds(Number(value), 0, 120),
          );
        },
      },

      topPadding: {
        get() {
          return this.value.object.tenants.topPadding === undefined
            ? 20
            : this.value.object.tenants.topPadding;
        },
        set(value) {
          this.$set(
            this.value.object.tenants,
            'topPadding',
            restrictToBounds(Number(value), 0, 120),
          );
        },
      },

      addressText: {
        get() {
          return this.value.object.tenants.addressText || this.value.object.tenants.address?.name;
        },
        set(value) {
          this.$set(this.value.object.tenants, 'addressText', value);
        },
      },

      addressTextSize: {
        get() {
          return this.value.object.tenants.addressFontSize || 24;
        },
        set(value) {
          this.$set(
            this.value.object.tenants,
            'addressFontSize',
            restrictToBounds(Number(value), 4, 120),
          );
        },
      },

      addressVerticalPadding: {
        get() {
          return this.value.object.tenants.addressVerticalPadding || 10;
        },
        set(value) {
          this.$set(
            this.value.object.tenants,
            'addressVerticalPadding',
            restrictToBounds(Number(value), 0, 99),
          );
        },
      },

      addressHorizontalPadding: {
        get() {
          return this.value.object.tenants.addressHorizontalPadding || 20;
        },
        set(value) {
          this.$set(
            this.value.object.tenants,
            'addressHorizontalPadding',
            restrictToBounds(Number(value), 0, 99),
          );
        },
      },
    },

    methods: {
      updateAllFloors() {
        const areAllFloorsVisible = this.value.object.tenants.floors.every((f) => f.visible);

        this.$set(this.value.object.tenants, 'allFloorsVisible', areAllFloorsVisible);
      },

      async fetchTenants() {
        try {
          const response = await apiGetDetailedAddress(this.value.object.tenants.address?.id);
          const floors = response.floors.map((floor) => {
            const floorDefaultName = floor.floor !== 0 ? `VÅNING ${floor.floor}` : 'Bottenvåning';

            return {
              ...floor,
              displayFloorName: floor.displayFloorName || floorDefaultName,
              visible: true,
            };
          });
          this.$set(this.value.object.tenants, 'floors', floors);
        } catch (error) {
          this.$toasted.global.general_error({
            message: 'Could not get tenants information.',
          });
        }
      },

      updateFloorDisplayName(floor, event) {
        let { value } = event.target;

        if (value.length > 40) {
          value = value.slice(0, 40);
        }

        this.$set(floor, 'displayFloorName', value);
      },

      async getAddresses() {
        try {
          this.loadingAddresses = true;
          const response = await apiGetAddresses();

          this.organisationAddresses = response.map((building) => ({
            id: building.externalId,
            name: building.streetAddress,
          }));

          this.organisationAddresses.unshift(demoTenantAddress);
        } catch (error) {
          this.$toasted.global.general_error({
            message: "Error getting your organisation's addresses",
          });
        }

        this.loadingAddresses = false;
      },

      updateTenantsInterval(value) {
        this.$set(this.value.object.tenants, 'interval', value);
      },

      handleAddressChanged({ id }) {
        this.$set(this.value.object.tenants, 'allFloorsVisible', true);

        if (id === 'demo') {
          this.$set(this.value.object.tenants, 'floors', tenantsDemoData);
          return;
        }

        this.fetchTenants();
      },

      updateTenantsFloors(value) {
        this.$set(this.value.object.tenants, 'floorsOnSlide', value);
      },

      updateTenantsFont(newFont, property) {
        this.$set(this.value.object.tenants, property, newFont);
      },

      updateColor(color) {
        if (!this.pickerColorKey) return;

        this.$set(this.value.object.tenants, this.pickerColorKey, color.hex8);
      },

      openColorPicker(colorKey, type) {
        if (this.isWidgetLockedByOverride || !type) return;

        if (type === 'title') {
          this.showTitleColorPicker = true;
        } else if (type === 'floors') {
          this.showFloorsColorPicker = true;
        } else if (type === 'rooms') {
          this.showRoomsColorPicker = true;
        } else if (type === 'pagination') {
          this.showPaginationColorPicker = true;
        } else {
          return;
        }

        this.pickerColorKey = colorKey;
      },

      closeTitleColorPicker() {
        if (!this.pickerColorKey || !this.showTitleColorPicker) return;

        this.showTitleColorPicker = false;
        this.pickerColorKey = null;
      },

      closeRoomsColorPicker() {
        if (!this.pickerColorKey || !this.showRoomsColorPicker) return;

        this.showRoomsColorPicker = false;
        this.pickerColorKey = null;
      },

      closePaginationColorPicker() {
        if (!this.pickerColorKey || !this.showPaginationColorPicker) return;

        this.showPaginationColorPicker = false;
        this.pickerColorKey = null;
      },

      closeFloorsColorPicker() {
        if (!this.pickerColorKey || !this.showFloorsColorPicker) return;

        this.showFloorsColorPicker = false;
        this.pickerColorKey = null;
      },

      setVerticalMode(isVertical = false) {
        this.$set(this.value.object.tenants, 'verticalMode', isVertical);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .vertical-padding {
    margin-top: 10px;
    width: 40%;
  }

  .layer-widget-panel {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    margin-right: 0px;
    padding-right: 10px;

    .sub-section-title {
      padding-top: 4px;
      font-weight: 500;
      font-size: 14px;
      line-height: 24px;
      width: 100%;
    }
  }

  .picker {
    position: absolute;
    top: 40px;
    right: 0;
    z-index: 20;
  }

  .time-container {
    display: flex;
    gap: 16px;
  }

  .section-title {
    font-weight: bold;
    padding-top: 16px;
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 10px;
    color: $tableText;
  }

  .buttons-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .slider-container {
    width: 100%;
    padding-right: 16px;
  }

  .measurements-container {
    padding-top: 6px;
  }

  .floors-container {
    border-radius: 8px;
    padding: 10px;
    border: 2px solid $borderGrey;
    max-height: 200px;
    overflow-y: auto;

    .custom-switch {
      margin-right: -8px;
    }

    .all-floors {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 8px;

      font-size: 14px;
      line-height: 20px;
      font-weight: bold;
      color: $primaryText;
    }

    .floor {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;

      &:hover {
        background-color: $highlightGrey;
      }

      .left-section {
        display: flex;
        justify-content: start;
        align-items: center;
        gap: 8px;
        width: 100%;
      }

      .drag-icon {
        padding: 2px;

        i {
          color: $secondaryText;
          opacity: 0.8;
          transform: scaleX(0.6);
          cursor: move;
        }
      }

      .edit-icon {
        display: none;

        &:hover {
          cursor: pointer;
        }
      }

      input.floor-title {
        width: 100%;
        font-size: 14px;
        line-height: 20px;
        font-weight: bold;
        color: $primaryText;
        border: none;
        outline: none !important;
        background: none;
        text-overflow: ellipsis;
      }

      .left-section:hover .edit-icon {
        display: inline;
        font-size: 10px;
      }
    }
  }

  .form-range {
    display: flex;
    gap: 16px;
    flex-direction: column;

    &.form-group {
      margin-bottom: 20px;
    }
  }

  .time-container {
    display: flex;
    gap: 16px;
  }

  .alignments {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;

    .alignment-mode {
      width: 26px;
      margin: 0 2px;

      input:checked + label {
        color: $primaryText;
        background: #f5f5f5;
        padding: 16px;
      }

      input + label {
        width: 30px;
        height: 30px;
        border-radius: 4px;
        color: $secondaryText;
      }

      .alignment-label {
        cursor: pointer;
      }
    }
  }

  .color-section {
    display: flex;
    gap: 10px;
    align-items: end;
    position: relative;
  }

  .title-row,
  .room-row,
  .pagination-row,
  .address-row,
  .floor-row {
    display: grid;
    position: relative;
    gap: 10px;
    margin: 0 0 1rem;

    .form-group {
      margin-bottom: 0;
    }
  }

  .pagination-row,
  .floor-row,
  .room-row {
    grid-template-columns: 170px 70px 40px;
  }

  .title-row,
  .address-row {
    grid-template-columns: 120px 70px 90px;
  }

  .group-text-switch,
  .hide-number-switch,
  .short-naming-switch,
  .pagination-switch,
  .number-position-switch {
    display: flex;
    margin-bottom: 8px;
  }

  .rotate-icon {
    transform: rotate(90deg);
    transform-origin: center;
    height: 24px;
  }

  .address-options {
    display: flex;
    gap: 16px;
    justify-content: space-between;

    .padding {
      border-radius: 8px;
      display: flex;
      gap: 8px;
    }

    .input-group {
      display: flex;
      flex-wrap: nowrap;
      font-family: $mainFontFamiliy;
      gap: 4px;
      align-items: center;
      padding-left: 4px;
      padding-right: 4px;
      max-width: 90px;

      &:focus-within,
      &:focus-visible {
        outline: none !important;
        border-color: transparent !important;
      }

      .input-label {
        background: #f3f3f3;
        padding: 0 4px;
        height: 100%;
        border-radius: 8px;
      }

      input {
        padding-left: 4px;
        padding-right: 4px;
      }
    }
  }

  .spacing {
    display: flex;
    gap: 16px;
  }
</style>
